import React, { useState, memo, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext, FieldArray } from 'formik';
import { Row, Col, Divider, Typography } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Starttask } from '../../../Slices/timerslice';
import { updateWorkHistoryThunk } from "../../../Slices/orderSlice";
import { timerHistoryPayloadChange } from '../../../Slices/user';
import { settaskStartTime } from "../../../Slices/user";
import { addLastContactedDate, setLastContactedDates } from "../../../Slices/lastContactedDatesSlice";
import { getAllLeadWorkHistory, setIsRefetch } from '../../../Slices/salesSlice';
import { InputField, TimePickerField, AntdButton, DatePickerField, SelectInput, TextareaField } from '../../../blueplanitUtils/InputField';
import CommentField from "./CommentField";
import { getOptionsByPath } from '../../../utilsdata';
import { LINKEDIN_TRACK, REQUEST_STATUS } from '../../../Dropdownmanager';

dayjs.extend(utc);
dayjs.extend(timezone);

const LinkedinTrack = memo((props) => {
  const { SalesInfo, assignToUsers, setFieldValue, id, leadID } = props;
  const CurrentTask = 'Linkedin';
  const dispatch = useDispatch();
  const { values, handleChange } = useFormikContext();

  const task = useSelector((state) => state.timer.task);
  const lastContactedDates = useSelector(state => state.lastContactedDates);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const isRefetch = useSelector((state) => state.sales.isRefetch);
  const salesLeadId = leadID;
  const WorkingId = useSelector((state) => state?.timer?.requestId);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  const userId = loginUser?.id;
  const loginUserName = useMemo(
    () => loginUser ? `${loginUser.firstname} ${loginUser.lastname}` : '',
    [loginUser]
  );

  const [requestTo, setRequestTo] = useState([]);
  const [remarks, setRemarks] = useState([]);
  
  const isInitialized = useRef(false);
  const prevLengthRef = useRef(0);
  const syncTimeout = useRef(null);


  useEffect(() => {
    if (!isInitialized.current && values?.leadsLinkedins) {
      setRequestTo(values.leadsLinkedins.map(item => item?.requestTo || ''));
      setRemarks(values.leadsLinkedins.map(item => item?.remarks || ''));
      prevLengthRef.current = values.leadsLinkedins.length;
      isInitialized.current = true;
    }
  }, [values?.leadsLinkedins]);


  useEffect(() => {
    if (isRefetch) {
      setRequestTo(values?.leadsLinkedins?.map(item => item?.requestTo || '') || []);
      setRemarks(values?.leadsLinkedins?.map(item => item?.remarks || '') || []);
      prevLengthRef.current = values?.leadsLinkedins?.length || 0;
      dispatch(setIsRefetch(false));
    }
  }, [isRefetch, values?.leadsLinkedins, dispatch]);

  useEffect(() => {
    const currentLength = values?.leadsLinkedins?.length || 0;
    if (currentLength > prevLengthRef.current) {
      setRequestTo(prev => {
        const newArray = [...prev];
        for (let i = prev.length; i < currentLength; i++) {
          newArray[i] = values.leadsLinkedins[i]?.requestTo || '';
        }
        return newArray;
      });
      setRemarks(prev => {
        const newArray = [...prev];
        for (let i = prev.length; i < currentLength; i++) {
          newArray[i] = values.leadsLinkedins[i]?.remarks || '';
        }
        return newArray;
      });
    }
    prevLengthRef.current = currentLength;
  }, [values?.leadsLinkedins?.length]);


  useEffect(() => {
    clearTimeout(syncTimeout.current);
    
    syncTimeout.current = setTimeout(() => {
      requestTo.forEach((value, index) => {
        if (values?.leadsLinkedins?.[index]?.requestTo !== value) {
          setFieldValue(`leadsLinkedins[${index}].requestTo`, value, false);
        }
      });
      remarks.forEach((value, index) => {
        if (values?.leadsLinkedins?.[index]?.remarks !== value) {
          setFieldValue(`leadsLinkedins[${index}].remarks`, value, false);
        }
      });
    }, 300);

    return () => clearTimeout(syncTimeout.current);
  }, [requestTo, remarks, values?.leadsLinkedins, setFieldValue]);

  // Safety net: flush pending changes on unmount
  useEffect(() => {
    return () => {
      clearTimeout(syncTimeout.current);
      requestTo.forEach((value, index) => {
        if (values?.leadsLinkedins?.[index]?.requestTo !== value) setFieldValue(`leadsLinkedins[${index}].requestTo`, value, false);
      });
      remarks.forEach((value, index) => {
        if (values?.leadsLinkedins?.[index]?.remarks !== value) setFieldValue(`leadsLinkedins[${index}].remarks`, value, false);
      });
    };
  }, [requestTo, remarks, values?.leadsLinkedins, setFieldValue]);

  const handleStartButtonClick = useCallback(async () => {
    const currentLeadID = SalesInfo?.leadID || salesLeadId;
    const payload = {
      department: "Sales",
      task: "Linkedin",
      time: Date.now(),
      requestId: currentLeadID,
    };

    try {
      const valuesObject = {
        userId,
        emailId: SalesInfo?.id || '',
        requestId: currentLeadID?.toString(),
        clientName: "Blueplanit",
        processType: "",
        taskType: "Linkedin",
        email: "",
        subject: "",
        body: "",
        startTime: Date.now().toString(),
        endTime: "",
        department: "Sales",
      };
      await dispatch(updateWorkHistoryThunk(valuesObject)).unwrap();
    } catch (err) {
      console.error("Error updating work history:", err);
    }

    const timestamp = Date.now();
    localStorage.setItem("task", "Linkedin");
    localStorage.setItem("time", timestamp);
    localStorage.setItem("requestId", currentLeadID);
    localStorage.setItem("department", "Sales");
    dispatch(Starttask(payload));
  }, [SalesInfo, salesLeadId, userId, dispatch]);

  const handleStop = useCallback(async () => {
    const valuesObject = {
      id: Math.random().toString(),
      userId,
      title: "",
      description: "",
      clientName: "Blueplanit",
      department: loginUser.department,
      taskType: "Available",
      startTime: Date.now().toString(),
      endTime: Date.now().toString(),
      requestId: "",
    };
    dispatch(timerHistoryPayloadChange({ data: valuesObject }));

    const values = {
      userId,
      emailId: "",
      requestId: "",
      clientName: "Blueplanit",
      processType: "",
      taskType: "Available",
      email: "",
      subject: "",
      body: "",
      startTime: Date.now().toString(),
      endTime: "",
      department: loginUser.department
    };
    await dispatch(updateWorkHistoryThunk(values)).unwrap();

    const payload = {
      department: "",
      task: "Available",
      time: Date.now(),
      requestId: '',
    };

    const timestamp = new Date().getTime();
    localStorage.setItem("time", timestamp);
    localStorage.setItem("task", 'Available');
    localStorage.removeItem("department");
    localStorage.removeItem("requestId");
    dispatch(settaskStartTime(timestamp));
    dispatch(Starttask(payload));
    dispatch(getAllLeadWorkHistory(SalesInfo?.id));
  }, [userId, loginUser, SalesInfo, dispatch]);

  const handleRequestToChange = useCallback((e, index) => {
    const { value } = e.target;
    setRequestTo(prev => {
      const newArray = [...prev];
      newArray[index] = value;
      return newArray;
    });
    // Immediate sync to Formik
    setFieldValue(`leadsLinkedins[${index}].requestTo`, value);
  }, [setFieldValue]);

  const handleRemarksChange = useCallback((e, index) => {
    const { value } = e.target;
    setRemarks(prev => {
      const newArray = [...prev];
      newArray[index] = value;
      return newArray;
    });
    // Immediate sync to Formik
    setFieldValue(`leadsLinkedins[${index}].remarks`, value);
  }, [setFieldValue]);

  const handleAddRow = useCallback((arrayHelpers) => {
    const newDate = dayjs();
    setFieldValue("lastContactedDate", newDate);
    
    arrayHelpers.push({
      date: newDate,
      time: dayjs().set('second', 0).set('millisecond', 0),
      status: 'Request Sent',
      requestTo: '',
      requestBy: loginUserName,
      remarks: '',
    });

    dispatch(addLastContactedDate(newDate.toISOString()));
  }, [loginUserName, setFieldValue, dispatch]);

  const handleDeleteRow = useCallback((index, arrayHelpers) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this Linkedin record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        arrayHelpers.remove(index);
        setRequestTo((prev) => prev.filter((_, i) => i !== index));
        setRemarks((prev) => prev.filter((_, i) => i !== index));

        const deletedRowDate = arrayHelpers.form.values.leadsLinkedins[index].date;
        const lastContactedDatess = lastContactedDates.filter(
          date => dayjs(date).valueOf() !== dayjs(deletedRowDate).valueOf()
        );

        const latestDate = lastContactedDatess.length > 0
          ? Math.max(...lastContactedDatess.map(date => dayjs(date).valueOf()))
          : null;

        dispatch(setLastContactedDates(lastContactedDatess));

        if (latestDate) {
          setFieldValue("lastContactedDate", dayjs(latestDate));
        }
      }
    });
  }, [lastContactedDates, setFieldValue, dispatch]);

  const getTime = useCallback((time) => {
    if (!time) return null;
    if (typeof time === "object" && dayjs.isDayjs(time)) return time;
    if (typeof time === "string") return dayjs.utc(time).local();
    return null;
  }, []);

  const getDateValue = useCallback((datevalue) => {
    if (!datevalue || datevalue === '') return null;
    if (typeof datevalue === 'string' && datevalue.length === 13) {
      return dayjs(parseInt(datevalue));
    }
    if (typeof datevalue === 'object') return datevalue;
    return null;
  }, []);

  // Memoized options
  const requestStatusOptions = useMemo(
    () => getOptionsByPath(NestedDatabucket, [LINKEDIN_TRACK, REQUEST_STATUS]),
    [NestedDatabucket]
  );

  const userOptions = useMemo(
    () => assignToUsers?.map((user) => ({
      key: user.id,
      label: `${user.firstname} ${user.lastname}`,
      value: `${user.firstname} ${user.lastname}`
    })) || [],
    [assignToUsers]
  );

  const showStopButton = id !== undefined && WorkingId === leadID && task === CurrentTask;

  return (
    <div style={{ 
      marginTop: '15px', 
      backgroundColor: "#fff", 
      padding: '20px', 
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', 
      borderRadius: '8px' 
    }}>
      <Typography.Title level={4}>
        LinkedIn Track
        {id !== undefined && (
          showStopButton ? (
            <AntdButton
              label="Stop"
              className="btn btn-danger"
              style={{
                float: "right",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={handleStop}
            />
          ) : (
            <AntdButton
              label="Start"
              color="primary"
              variant="solid"
              style={{
                float: "right",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={handleStartButtonClick}
            />
          )
        )}
      </Typography.Title>

      <FieldArray
        name="leadsLinkedins"
        render={(arrayHelpers) => (
          <>
            <Row justify="end" style={{ marginBottom: '8px' }}>
              <AntdButton
                label="Add Row"
                type="primary"
                onClick={() => handleAddRow(arrayHelpers)}
              />
            </Row>
            {values?.leadsLinkedins?.map((LinkedInInfo, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                <Row gutter={16}>
                  <Col span={24} style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '15px'
                  }}>
                    <Typography.Text strong>Connection No. {index + 1}</Typography.Text>
                    <i
                      className="fa-regular fa-trash-can"
                      style={{
                        color: '#00c5fb',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                      onClick={() => handleDeleteRow(index, arrayHelpers)}
                    />
                  </Col>

                  <Col span={4}>
                    <DatePickerField
                      label="Date"
                      inputReadOnly
                      allowClear={false}
                      format="DD/MM/YYYY"
                      name={`leadsLinkedins.${index}.date`}
                      value={getDateValue(values.leadsLinkedins?.[index]?.date)}
                      onChange={(date) => setFieldValue(`leadsLinkedins.${index}.date`, date)}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col span={4}>
                    <TimePickerField
                      label="Time"
                      name={`leadsLinkedins.${index}.time`}
                      value={LinkedInInfo?.time && getTime(LinkedInInfo?.time)}
                      onChange={(time) =>
                        handleChange({
                          target: { value: time, name: `leadsLinkedins.${index}.time` },
                        })
                      }
                      format="h:mm A"
                      allowClear={false}
                      inputReadOnly
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col span={4}>
                    <SelectInput
                      style={{ width: "100%" }}
                      label="Request Status"
                      value={values.leadsLinkedins[index]?.status || 'Request Sent'}
                      onChange={(value) => setFieldValue(`leadsLinkedins.${index}.status`, value)}
                      options={requestStatusOptions}
                      placeholder="Select Request Status"
                      showSearch
                    />
                  </Col>

                  <Col span={4}>
                    <InputField
                      label='Request To'
                      value={requestTo[index] || ''}
                      onChange={(e) => handleRequestToChange(e, index)}
                    />
                  </Col>

                  <Col span={4}>
                    <SelectInput
                      style={{ width: "100%" }}
                      label="Request By"
                      value={values.leadsLinkedins[index]?.requestBy || ''}
                      onChange={(value) => setFieldValue(`leadsLinkedins.${index}.requestBy`, value)}
                      options={userOptions}
                      placeholder="Select Request By"
                      showSearch
                    />
                  </Col>

                  <Col span={22}>
                    <TextareaField
                      label="Remarks"
                      value={remarks[index] || ''}
                      onChange={(e) => handleRemarksChange(e, index)}
                      rows={2}
                    />
                  </Col>
                </Row>

                <Divider style={{ backgroundColor: '#ccc', height: '1px' }} />
              </div>
            ))}

          </>
        )}
      />
      <CommentField label="LinkedIn Comments" name="linkedinComments" />
    </div>
  );
});

export default LinkedinTrack;